import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { SITE_CONFIG } from "../site-config";
import { formatMoney, normalizeUrl } from "../lib";
import type { CheckoutResponse, SiteId, SiteState, Slot } from "../types";
import "../styles/claim-dialog.css";

interface ClaimDialogProps {
  site: SiteId;
  slot: Slot;
  state: SiteState;
  onClose: () => void;
}

export default function ClaimDialog({ site, slot, state, onClose }: ClaimDialogProps) {
  const config = SITE_CONFIG[site];
  const currentLeader = slot.entries[0];
  const minimumDollars = state.minimumBidCents / 100;
  const [url, setUrl] = useState("");
  const [amount, setAmount] = useState(String(minimumDollars));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [challengeReady, setChallengeReady] = useState(false);
  const amountWasEdited = useRef(false);
  const dialogRef = useRef<HTMLElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(
    typeof document !== "undefined" && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null,
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    const focusableSelector = [
      "button:not([disabled])",
      "a[href]",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "altcha-widget",
      "[tabindex]:not([tabindex='-1'])",
    ].join(",");

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const focusable = [...dialog.querySelectorAll<HTMLElement>(focusableSelector)]
        .filter((element) => !element.hasAttribute("disabled") && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        dialog.focus();
      } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedRef.current?.focus({ preventScroll: true });
    };
  }, [onClose]);

  useEffect(() => {
    if (!state.stripeEnabled) return;
    let active = true;
    void import("altcha")
      .then(() => {
        if (active) setChallengeReady(true);
      })
      .catch(() => {
        if (active) setError("The human-ish checkpoint did not load. Refresh and try again.");
      });
    return () => {
      active = false;
    };
  }, [state.stripeEnabled]);

  const projection = useMemo(() => {
    const amountCents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) return null;
    const normalized = normalizeUrl(url);
    const existing = slot.entries.find((entry) => normalizeUrl(entry.url) === normalized);
    const cumulative = amountCents + (existing?.amountCents ?? 0);
    const others = slot.entries.filter((entry) => entry.id !== existing?.id);
    const rank = others.filter((entry) => entry.amountCents >= cumulative).length + 1;
    return { rank, cumulative, previous: existing?.amountCents ?? 0 };
  }, [amount, slot.entries, url]);

  const existingEntry = slot.entries.find((entry) => normalizeUrl(entry.url) === normalizeUrl(url));
  const existingUrlTotal = existingEntry?.amountCents ?? 0;
  const centsToBeat = Math.max(0, (currentLeader?.amountCents ?? 0) - existingUrlTotal);
  const winningAddOnCents = Math.max(
    state.minimumBidCents,
    Math.ceil((centsToBeat + 1) / 100) * 100,
  );

  useEffect(() => {
    if (!amountWasEdited.current) {
      setAmount(String((existingEntry ? winningAddOnCents : state.minimumBidCents) / 100));
    }
  }, [existingEntry, state.minimumBidCents, winningAddOnCents]);

  function chooseAmount(nextAmount: string): void {
    amountWasEdited.current = true;
    setAmount(nextAmount);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const form = event.currentTarget as HTMLFormElement;
    const challengeProof = new FormData(form).get("altcha");
    if (typeof challengeProof !== "string" || !challengeProof) {
      setError("Finish the human-ish check before opening checkout.");
      return;
    }
    const amountCents = Math.round(Number(amount) * 100);
    if (!Number.isSafeInteger(amountCents) || amountCents < state.minimumBidCents || amountCents % 100 !== 0) {
      setError(`Enter a whole-dollar bid of at least ${formatMoney(state.minimumBidCents)}.`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site,
          slotId: slot.id,
          url,
          amountCents,
          altcha: challengeProof,
        }),
      });
      const result = (await response.json()) as CheckoutResponse & { error?: string };
      if (!response.ok || !result.url) throw new Error(result.error ?? "Checkout did not start.");
      if (
        /^cs_(?:test_|live_)?[A-Za-z0-9]+$/.test(result.sessionId) &&
        /^[0-9a-z]{1,12}\.[0-9a-z]{1,12}\.[A-Za-z0-9_-]{43}$/.test(result.confirmationToken)
      ) {
        try {
          window.sessionStorage.setItem(`outbid-confirm:${result.sessionId}`, result.confirmationToken);
        } catch {
          // Stripe's signed webhook remains authoritative if storage is unavailable.
        }
      }
      // Always continue to an already-created Checkout Session. If the private
      // receipt handoff failed, the success view falls back to webhook processing.
      window.location.assign(result.url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Checkout did not start.");
      form.querySelector("altcha-widget")?.reset();
      setSubmitting(false);
    }
  }

  return (
    <div className="claim-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} className="claim-dialog" role="dialog" aria-modal="true" aria-labelledby="claim-title" tabIndex={-1}>
        <button className="claim-close" type="button" onClick={onClose} aria-label="Close bid form">×</button>
        <div className="claim-kicker">{config.domain} · {slot.label}</div>
        <h2 id="claim-title">Claim {slot.label}.</h2>
        <p className="claim-explainer">Add your URL and choose a bid. Higher lifetime totals rank first; every paid bidder stays listed.</p>

        {slot.entries.length > 0 && (
          <div className="claim-current">
            <span>Current #1</span>
            <strong>{currentLeader.name}</strong>
            <b>{formatMoney(currentLeader.amountCents)}</b>
          </div>
        )}

        <form onSubmit={submit}>
          <div className="claim-fields">
            <label className="claim-field-wide">
              <span>Website URL</span>
              <input value={url} onChange={(event) => setUrl(event.target.value)} maxLength={500} required inputMode="url" autoComplete="url" placeholder="your.website" autoFocus />
            </label>
            <label className="claim-field-wide">
              <span>Your bid (USD)</span>
              <div className="claim-money-field"><i>$</i><input value={amount} onChange={(event) => chooseAmount(event.target.value)} type="number" min={minimumDollars} max="10000" step="1" required /></div>
            </label>
          </div>

          {currentLeader && (
            <div className="claim-presets">
              <button type="button" onClick={() => chooseAmount(String(minimumDollars))}>Join the ranking · {formatMoney(state.minimumBidCents)}</button>
              <button type="button" onClick={() => chooseAmount(String(winningAddOnCents / 100))}>Target #1 · {formatMoney(winningAddOnCents)}</button>
            </div>
          )}

          {currentLeader && projection && (
            <div className="claim-projection">
              <span>Estimated rank · not reserved</span>
              <strong>#{projection.rank}</strong>
              <small>{projection.previous > 0 ? `${formatMoney(projection.previous)} existing + ` : ""}{formatMoney(Math.round(Number(amount) * 100))} = {formatMoney(projection.cumulative)}</small>
            </div>
          )}

          {state.stripeEnabled && challengeReady && (
            <div className="claim-proof">
              <div>
                <strong>Quick security check</strong>
                <span>No account. No tracking.</span>
              </div>
              <altcha-widget
                auto="onsubmit"
                challenge="/api/checkout/challenge"
                configuration='{"minDuration":800,"validationMessage":"Complete the human-ish checkpoint."}'
                name="altcha"
                type="checkbox"
                workers="4"
              />
            </div>
          )}
          {state.stripeEnabled && !challengeReady && (
            <p className="claim-proof-loading" role="status">Warming up the human-ish checkpoint…</p>
          )}

          {error && <p className="claim-error" role="alert">{error}</p>}
          {!state.stripeEnabled && <p className="claim-error" role="alert">Checkout is not connected on this deployment yet.</p>}
          <button
            className="claim-submit"
            type="submit"
            disabled={submitting || !state.stripeEnabled || !challengeReady}
          >
            {submitting ? "Opening Stripe…" : `Pay ${formatMoney(Math.round(Number(amount || 0) * 100))}${projection ? ` · enter around #${projection.rank}` : ""} →`}
          </button>
          <p className="claim-fineprint">Secure Stripe checkout. Rank may move before payment finishes. Getting outbid moves you down; it never triggers an automatic refund.</p>
        </form>
      </section>
    </div>
  );
}
